import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import RequiresActionDocumentList from 'components/clinical-data/RequiresActionDocumentList';
import RequiresActionSearchForm from 'components/clinical-data/RequiresActionSearchForm';
import FullScreenPageHeader from 'components/FullScreenPageHeader';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import LoaderComponent from 'components/LoaderComponent';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useRef, useState } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Message, Segment } from 'semantic-ui-react';
import { getAllDoctors } from 'services/doctorServices';
import {
  getPatientCdrDocumentsThatRequireAction,
  getPatientDocumentsWithProcedureCreatedFalse
} from 'services/patientCdrDocumentServices';
import { getAllPatientDocumentTypes } from 'services/patientDocumentTypeServices';
import { getUserDisplay } from 'services/userSettingsServices';
import styled from 'styled-components';
import {
  DoctorDto,
  PatientCdrDocumentDto,
  PatientDocumentTypeDto,
  PatientDto,
  UserDisplayDto
} from 'ts-types/api.types';
import { toIsoDateString } from 'utils/dateUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const MainContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  .page-header.page-header-ps {
    padding-top: 0;
    padding-bottom: 0.875rem !important;
  }

  .page-header .title {
    padding-left: 0.287rem;
    font-size: 1.1rem !important;
    color: #444444;
  }

  .ui.message {
    flex: 0 0 auto;
  }

  .table-container {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    margin-top: 0;
    overflow: auto;

    .search-form {
      flex: 0 0 auto;
    }

    .requires-action {
      color: #957212;
    }
  }

  & .document-list {
    margin-top: 0;
  }
`;

export interface RequiresActionSearchParams {
  requiresActionOnly: boolean;
  requiresDoctorActionOnly: boolean;
  abnormalFindingOnly: boolean;
  discussWithPatientOnly: boolean;
  noProcedureCreatedOnly: boolean;
  procedureCreatedOnly: boolean;
  timeFrame?: string;
  fromDate?: string;
  toDate?: string;
}

interface Props extends MessageSourceProps,
    AuthConsumerRenderProps,
    RouteComponentProps,
    PatientCentralRefDataConsumerRenderProps {

  displayRequiresAction: boolean
}

const ClinicalDataRecordRequiresAction = (props: Props) => {

  const {
    getMessage,
    history,
    displayRequiresAction
  } = props;

  const cancelTokenSource = useRef(axios.CancelToken.source());

  const unmounted = useRef(false);

  const [searchParams, setSearchParams] =
      useState<RequiresActionSearchParams>({
        requiresActionOnly: false,
        requiresDoctorActionOnly: false,
        abnormalFindingOnly: false,
        discussWithPatientOnly: false,
        noProcedureCreatedOnly: false,
        procedureCreatedOnly: false,
        timeFrame: "past_3_months",
        fromDate: toIsoDateString(moment().subtract(3, "month").set({date: 1})),
        toDate: toIsoDateString(moment())
      });

  const [patientDocuments, setPatientDocuments] =
      useState([] as PatientCdrDocumentDto[]);
  const [documentPatientMap, setDocumentPatientMap] =
      useState({} as { [index: string]: PatientDto });
  const [doctorsRecord, setDoctorsRecord] =
      useState({} as Record<string, DoctorDto>);

  const [patientDocumentTypesMap, setPatientDocumentTypesMap] =
      useState<{ [code: string]: PatientDocumentTypeDto }>({});

  const [initialDataLoaded, setInitialDataLoaded] =
      useState(false);
  const [errorMessage, setErrorMessage] =
      useState(undefined as string | undefined);
  const [successfulMessage, setSuccessfulMessage] =
      useState(undefined as string | undefined);

  const [currentUserDisplay, setCurrentUserDisplay] =
      useState<UserDisplayDto>({
        id: -1,
        firstName: "",
        name: ""
      });

  const fetchData = async (aSearchParams: RequiresActionSearchParams) => {
    setPatientDocuments([]);
    setInitialDataLoaded(false);

    try {

      // fetch document types
      const documentsTypes = await getAllPatientDocumentTypes(
          cancelTokenSource.current);

      // fetch doctors
      const doctors = await getAllDoctors(cancelTokenSource.current);
      let doctorsRecord: Record<string, DoctorDto> = {};
      doctors.forEach((doctor: DoctorDto) => {
        doctorsRecord[doctor.id] = doctor;
      });

      // fetch user data
      const userDisplayDto = await getUserDisplay(cancelTokenSource.current);

      // fetch the documents
      let documents: PatientCdrDocumentDto[] = [];
      let patientsMap: { [id: string]: PatientDto } = {};
      if (displayRequiresAction) {
        const patientCdrDocumentsThatRequireAction =
            await getPatientCdrDocumentsThatRequireAction(
                aSearchParams.requiresActionOnly,
                aSearchParams.requiresDoctorActionOnly,
                aSearchParams.abnormalFindingOnly,
                aSearchParams.discussWithPatientOnly,
                aSearchParams.noProcedureCreatedOnly,
                aSearchParams.procedureCreatedOnly,
                aSearchParams.fromDate,
                aSearchParams.toDate,
                cancelTokenSource.current
            );

        documents = patientCdrDocumentsThatRequireAction.patientCdrDocuments;
        patientsMap = patientCdrDocumentsThatRequireAction.documentPatientMap;
      } else {
        const patientCdrDocumentsThatRequireAction =
            await getPatientDocumentsWithProcedureCreatedFalse(
                aSearchParams.fromDate,
                aSearchParams.toDate,
                cancelTokenSource.current
            );

        documents = patientCdrDocumentsThatRequireAction.patientCdrDocuments;
        patientsMap = patientCdrDocumentsThatRequireAction.documentPatientMap;
      }

      if (!unmounted.current) {
        setPatientDocumentTypesMap(_.keyBy(documentsTypes, "code"));
        setDoctorsRecord(doctorsRecord);
        setCurrentUserDisplay(userDisplayDto);
        setDocumentPatientMap(patientsMap);
        setPatientDocuments(documents);
      }

    } catch (e) {
      // ignore
    } finally {
      if (!unmounted.current) {
        setInitialDataLoaded(true);
      }
    }
  }

  useEffect(() => {

    fetchData(searchParams);

    return (() => {
      unmounted.current = true;
      cancelTokenSource.current && cancelTokenSource.current.cancel();
    })
  }, []);

  const goToClinicalDataRecord = () => {
    history.push("/clinical-data-record");
  }

  return (
      <MainContentDiv>
        <FullScreenPageHeader
            className={"page-header-ps"}
            headerMessage={getMessage("clinicalData.home.title.main")}
            buttonMessage={getMessage("back")}
            onConfirm={() => goToClinicalDataRecord()}
        />
        {errorMessage && (
            <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
              {errorMessage}
            </StyledErrorMessage>
        )}
        {successfulMessage && (
            <Message success onDismiss={() => setSuccessfulMessage(undefined)}>
              {successfulMessage}
            </Message>
        )}
        {
            (!initialDataLoaded)
            && <LoaderComponent message={getMessage("loading")}/>
        }

        <Segment className="table-container">

          <RequiresActionSearchForm className={"search-form"}
                                    displayRequiresAction={displayRequiresAction}
                                    searchParams={searchParams}
                                    setSearchParams={setSearchParams}
                                    doSearch={(params: RequiresActionSearchParams) => {
                                      fetchData(params);
                                    }}
          />

          {
              initialDataLoaded
              && <RequiresActionDocumentList
                  className={"document-list"}
                  documents={patientDocuments}
                  patientsMap={documentPatientMap}
                  doctorsMap={doctorsRecord}
                  documentTypesMap={patientDocumentTypesMap}
                  currentUserDisplayData={currentUserDisplay}
                  setDocuments={(documents: PatientCdrDocumentDto[]) => setPatientDocuments(documents)}
                  onSuccess={setSuccessfulMessage}
                  onError={setErrorMessage}
              />
          }
        </Segment>
      </MainContentDiv>
  );
}

export default withPatientCentralRefDataContext(
    withAuthContext(
        withRouterWorkaround(
            withMessages(ClinicalDataRecordRequiresAction))));
